import { Box, Stack, Tabs } from '@evaneos/b2b-ui';
import AttachmentsIcon from '@evaneos/b2b-ui/icons/LibraryBooksOutlined';
import DiscussionIcon from '@evaneos/b2b-ui/icons/QuestionAnswerOutlined';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import {
    useTrackEventAmplitude,
    useGetTrackedTripProperties,
    useExperimentVariation,
} from '../../../shared/analytics/amplitude-hooks';

import { PanelTab } from '../../../design-system/PanelTab';

import { ERROR, PENDING } from '../../../design-system/constants';
import { Loader } from '../../../design-system/Loader';
import { getDraftMessageRequest } from '../../../resources/discussion';
import { getDraftMessageStatus as getDraftMessageStatusSelector } from '../../../resources/discussion/selectors';
import { DossierId } from '../../../resources/dossier/types';
import { useGetPhoneCalls } from '../../../resources/phoning/queries';
import { useAppDispatch, useAppSelector } from '../../../shared/redux';
import { ErrorOverlay } from '../../Pipeline/components/Error';
import { useGetDossierData } from '../../../resources/dossier/queries';
import { AB_TravelRhythmModal } from '../components/AB_TravelRhythmModal/AB_TravelRhythmModal';
import { useFilteredDiscussionGroupedByDate } from '../../../resources/discussion/queries';

import { AB_SpringFlexBadge } from '../AB_SpringFlexBadge';

import {
    useGetCalendarStatus,
    useGetDossierAppointments,
} from '../../../resources/appointment/queries';
import { useCancelAppointmentMutation } from '../../../resources/appointment/mutations';
import { isAnyProviderConnected } from '../../../resources/appointment/operations';

import { AppointmentBlock } from './components/appointment/AppointmentBlock';
import { AttachmentsTab } from './components/AttachmentsTab';
import { CallButtonGroup } from './components/CallButtonGroup';
import { DiscussionTab } from './components/DiscussionTab';
import s from './components/Discussion.module.css';

/* BEGIN - transient flight-cancellation-coverage - PR #2885 */
import { TransientFlightCancellationBadge } from '@transient/flight-cancellation-coverage';
/* END - transient flight-cancellation-coverage - PR #2885 */

type ActiveTab = 'discussion' | 'attachments';

export function Discussion() {
    const { dossierId } = useParams() as { dossierId: DossierId };
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    const urlParams = new URLSearchParams(location.search);
    const targetPhoneCallId = Number(urlParams.get('phoneCall'));
    const isFromNotification = urlParams.get('fromNotification') === 'true';

    useGetDossierData(dossierId);

    const { status: getDiscussionStatus, refetch: refetchDiscussion } =
        useFilteredDiscussionGroupedByDate(dossierId);
    const { status: getPhoneCallsStatus, refetch: refetchPhoneCalls } = useGetPhoneCalls(dossierId);
    const getDraftMessageStatus = useAppSelector(getDraftMessageStatusSelector(dossierId));

    const [activeTab, setActiveTab] = useState<ActiveTab>('discussion');
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const { data: calendarStatus } = useGetCalendarStatus();
    const { data: dossierAppointments } = useGetDossierAppointments(dossierId);
    const { mutate: cancelAppointment } = useCancelAppointmentMutation(dossierId);
    const currentAppointment = dossierAppointments?.[0];
    const handleCancelAppointment = useCallback(() => {
        if (currentAppointment) cancelAppointment(currentAppointment.id);
    }, [cancelAppointment, currentAppointment]);
    const calendarVariant = useExperimentVariation('ab-google-calendar');
    const showAppointmentBlock =
        calendarVariant?.value === 'on' || calendarVariant?.value === 'treatment';

    const handleTabChange = (_event: React.SyntheticEvent, value: ActiveTab) => {
        setActiveTab(value);

        if (value === 'attachments') {
            trackEvent((ampli, defaultProperties) => {
                ampli.listAttachmentsClicked({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
        }
    };

    if (
        getDiscussionStatus === PENDING ||
        getPhoneCallsStatus === PENDING ||
        getDraftMessageStatus === PENDING
    ) {
        return (
            <div className={s.discussionLoading} data-testid="dossier-loader">
                <Loader className={s.loader} />
            </div>
        );
    }

    if (getDiscussionStatus === ERROR) {
        return (
            <div className={s.discussionContainer}>
                <ErrorOverlay
                    onRefresh={() => {
                        void refetchDiscussion();
                        void refetchPhoneCalls();
                        dispatch(getDraftMessageRequest(dossierId));
                    }}
                />
            </div>
        );
    }

    return (
        <div className={s.discussionContainer}>
            <Stack
                direction="row"
                paddingX={3}
                paddingTop={3}
                borderBottom="1px solid"
                borderColor="divider"
                justifyContent="space-between"
                alignItems="baseline"
            >
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <PanelTab
                        value="discussion"
                        id="discussion-tab"
                        aria-controls="discussion-tabpanel"
                        label={formatMessage({ id: 'dossier.discussion.title' })}
                        icon={<DiscussionIcon fontSize="small" />}
                        iconPosition="start"
                    />
                    <PanelTab
                        value="attachments"
                        id="attachments-tab"
                        aria-controls="attachments-tabpanel"
                        data-spec-id="attachments-tab-button"
                        label={formatMessage({ id: 'discussion.attachments_list.open_button' })}
                        icon={<AttachmentsIcon fontSize="small" />}
                        iconPosition="start"
                    />
                </Tabs>
                <Box display="flex" alignItems="baseline" gap={1}>
                    {/* BEGIN - AB ab_flex_offer - PR #2760 */}
                    <Box alignSelf="center">
                        <AB_SpringFlexBadge dossierId={dossierId} />
                    </Box>
                    {/* END - AB ab_flex_offer - PR #2760 */}

                    {/* BEGIN - transient flight-cancellation-coverage - PR #2885 */}
                    <Box alignSelf="center">
                        <TransientFlightCancellationBadge dossierId={dossierId} />
                    </Box>
                    {/* END - transient flight-cancellation-coverage - PR #2885 */}

                    {/* Un RDV existant doit rester visible pour tout agent consultant le
                        dossier, qu'il ait connecté son agenda ou non (BBIZ-634). On laisse
                        donc AppointmentBlock décider quoi afficher au lieu de conditionner le
                        rendu au statut calendrier de l'agent courant. */}
                    {showAppointmentBlock && (
                        <Box alignSelf="center">
                            <AppointmentBlock
                                appointments={dossierAppointments}
                                isProviderConnected={isAnyProviderConnected(calendarStatus)}
                                onCancelAppointment={handleCancelAppointment}
                            />
                        </Box>
                    )}

                    <CallButtonGroup dossierId={dossierId} />
                </Box>
            </Stack>

            <Box
                role="tabpanel"
                id="discussion-tabpanel"
                aria-labelledby="discussion-tab"
                sx={{
                    display: activeTab === 'discussion' ? 'flex' : 'none',
                    flex: '1 1 auto',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <DiscussionTab
                    dossierId={dossierId}
                    isFromNotification={isFromNotification}
                    targetPhoneCallId={targetPhoneCallId}
                />
            </Box>
            <Box
                role="tabpanel"
                id="attachments-tabpanel"
                aria-labelledby="attachments-tab"
                sx={{
                    display: activeTab === 'attachments' ? 'flex' : 'none',
                    flex: '1 1 auto',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <AttachmentsTab dossierId={dossierId} isActive={activeTab === 'attachments'} />
            </Box>
            {/* BEGIN - AB ab_travel_rhythm - PR #TODO_AFTER_PR_CREATION_ab_travel_rhythm */}
            <AB_TravelRhythmModal dossierId={dossierId} />
            {/* END - AB ab_travel_rhythm - PR #TODO_AFTER_PR_CREATION_ab_travel_rhythm */}
        </div>
    );
}
